import { useState } from 'react';
import { useSelector } from 'react-redux';
import { CourierUserApi } from '@shared/sdk-v1/apis';
import { apiConfig } from '../../shared/api-config';
import { getCourier, getCourierRealm } from '../../state/courierSlice';
import { showErrorToast, showSuccessToast } from '../../utils/toast';

export const usePasswordReset = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const courier = useSelector(getCourier);
  const realm = useSelector(getCourierRealm);

  const requestPasswordReset = async (email: string) => {
    if (!courier?.id || !realm) {
      showErrorToast({
        title: 'Error',
        message: 'Courier configuration not found',
      });
      return { success: false };
    }

    try {
      setIsLoading(true);
      const authApi = new CourierUserApi(apiConfig());

      await authApi.courierUserResetPassword({
        options: {
          email: email,
          courierId: courier.id.toString(),
          redirectUrl: window.location.origin,
          realm: realm,
        },
      });

      setIsSubmitted(true);
      showSuccessToast({
        title: 'Success',
        message:
          'Password reset email sent. Please check your email for the reset link.',
      });
      return { success: true };
    } catch (error: any) {
      console.error('Password reset error:', error);
      showErrorToast({
        title: 'Error',
        message:
          error.response?.data?.error?.message ||
          'Failed to send password reset email',
      });
      return { success: false };
    } finally {
      setIsLoading(false);
    }
  };

  const resetPassword = async (
    accessToken: string,
    newPassword: string,
    confirmPassword: string
  ) => {
    if (newPassword !== confirmPassword) {
      showErrorToast({
        title: "Passwords Don't Match",
        message: 'Please make sure both passwords are the same.',
      });
      return { success: false };
    }

    if (newPassword.length < 6) {
      showErrorToast({
        title: 'Password Too Short',
        message: 'Password must be at least 6 characters long.',
      });
      return { success: false };
    }

    try {
      setIsLoading(true);

      const response = await fetch(
        `${
          apiConfig().basePath
        }/CourierUsers/reset-password?access_token=${accessToken}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            newPassword: newPassword,
            confirmation: confirmPassword,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error?.message || 'Failed to reset password');
      }

      showSuccessToast({
        title: 'Success',
        message:
          'Your password has been reset successfully. You can now log in with your new password.',
      });
      return { success: true };
    } catch (error: any) {
      console.error('Password reset error:', error);
      showErrorToast({
        title: 'Error',
        message:
          error.response?.data?.error?.message ||
          'Failed to reset password. The link may have expired.',
      });
      return { success: false };
    } finally {
      setIsLoading(false);
    }
  };

  return {
    isLoading,
    isSubmitted,
    requestPasswordReset,
    resetPassword,
  };
};
